<template>
  <Component :is="continuous ? 'div' : BaseCard" :class="containerClasses" id="candidateHighlights">
    <p class="w-24 bg-gradient-to-r bg-clip-text font-bold text-transparent from-bdBlue-600 to-bdPurple-600">
      Highlights.
    </p>

    <!--    About Me    -->
    <EditableParagraphQuote :editable="editable"
                            :modelValue="candidate.about"
                            fieldName="About Me"
                            :showRecommendedCompletion="editable"
                            :maxLength="candidateBasicInfo.aboutMe.maxLength"
                            @update:modelValue="handleUpdateCandidate('about', $event)"/>

    <!--    Profile Videos    -->
    <div v-show="hasAtLeastOneVideoOrIsEditing" class="mt-8 flex flex-wrap gap-6">

      <EditableProfileVideo ref="profileVideo1"
                            field-name="Profile Video 1"
                            :editable="editable"
                            :questions="candidateVideo.profileVideo1.prompts"
                            :video-questions="candidateVideo.profileVideo1.videoPrompts"
                            :path="candidate.profileVideo1?.path"
                            :prompt="candidate.profileVideo1?.prompt"
                            @update:path="handleUpdateCandidate('profileVideo1', {...candidate?.profileVideo1, path: $event})"
                            @update:prompt="handleUpdateCandidate('profileVideo1', {...candidate?.profileVideo1, prompt: $event})"
                            @update:deleted="handleUpdateCandidate('profileVideo1', {...candidate?.profileVideo1, deleted: $event})"
                            @update:file="handleUpdateCandidate('profileVideo1', {...candidate?.profileVideo1, file: $event})"
                            @play="setFullscreenVideoSrc($event)"/>

      <EditableProfileVideo ref="profileVideo2"
                            field-name="Profile Video 2"
                            :editable="editable"
                            :questions="candidateVideo.profileVideo2.prompts"
                            :video-questions="candidateVideo.profileVideo2.videoPrompts"
                            :path="candidate.profileVideo2?.path"
                            :prompt="candidate.profileVideo2?.prompt"
                            @update:path="handleUpdateCandidate('profileVideo2', {...candidate?.profileVideo2, path: $event})"
                            @update:prompt="handleUpdateCandidate('profileVideo2', {...candidate?.profileVideo2, prompt: $event})"
                            @update:deleted="handleUpdateCandidate('profileVideo2', {...candidate?.profileVideo2, deleted: $event})"
                            @update:file="handleUpdateCandidate('profileVideo2', {...candidate?.profileVideo2, file: $event})"
                            @play="setFullscreenVideoSrc($event)"/>

      <UtilityFullscreenVideoPlayer v-model:src="fullscreenVideoSrc" v-model:prompt="fullscreenVideoPrompt"
                                    :muted="muted" :title="`${candidate.firstName || 'Candidate'}'s Profile Videos`"/>
    </div>

    <FormButton v-if="!hasAtLeastOneVideoOrIsEditing && editable" color="gradient" @click="addProfileVideo" flex
                class="mt-6 block">
      <PlusCircleIcon class="mr-1 h-4 w-4"/>
      <span>Add Profile Video</span>
    </FormButton>

    <!--    Exceptional Ability    -->
    <EditableStringList :editable="editable"
                        :modelValue="props.candidate.exceptionalAbility"
                        fieldName="Exceptional Abilities"
                        @update:modelValue="handleUpdateCandidate('exceptionalAbility', $event)"/>

    <!--    Question/Answer (Summary)   -->
    <EditableQuestionAnswer :editable="editable"
                            :modelValue="candidate.summary"
                            fieldName="Interesting Question/Answer"
                            @update:modelValue="handleUpdateCandidate('summary', $event)"
                            :questions="candidateBasicInfo.prompt.prompts"
                            class="mt-6 mb-4"/>

    <!--    Empty Profile   -->
    <p v-if="notEditableAndCompletelyEmpty"
       class="mt-6 mb-2 text-sm text-slate-400">
      No highlights yet
    </p>
  </Component>
</template>

<script setup>
import BaseCard from "./BaseCard.vue";
import FormButton from "./FormButton.vue";
import UtilityFullscreenVideoPlayer from "./UtilityFullscreenVideoPlayer.vue";
import EditableParagraphQuote from "./EditableParagraphQuote.vue";
import EditableProfileVideo from "./EditableProfileVideo.vue";
import EditableStringList from "./EditableStringList.vue";
import EditableQuestionAnswer from "./EditableQuestionAnswer.vue";
import {computed, ref} from "vue";
import {PlusCircleIcon} from "@heroicons/vue/24/solid/index.js";
import {candidateBasicInfo, candidateVideo} from "../helpers/ProfileQuestions.js";

const emits = defineEmits(["update:candidate"]);
const props = defineProps({
  candidate: Object,
  continuous: Boolean,
  muted: Boolean,
  small: Boolean,
  editable: Boolean,
});

const fullscreenVideoSrc = ref("");
const fullscreenVideoPrompt = ref("");
const profileVideo1 = ref(null);
const profileVideo2 = ref(null);

const setFullscreenVideoSrc = (video) => {
  fullscreenVideoSrc.value = video?.path;
  fullscreenVideoPrompt.value = video?.prompt;
};

const handleUpdateCandidate = (key, value) => {
  if (!props.editable) return;

  const candidate = {...props.candidate};
  candidate[key] = value;
  emits("update:candidate", candidate);
};

const addProfileVideo = () => {
  if (!props.editable) return;

  if (!props.candidate.profileVideo1?.path) profileVideo1.value?.setIsEditing(true);
  else if (!props.candidate.profileVideo2?.path) profileVideo2.value?.setIsEditing(true);
};

const hasAtLeastOneVideoOrIsEditing = computed(() => {
  return props.candidate.profileVideo1?.path || props.candidate.profileVideo2?.path || profileVideo1.value?.isEditing || profileVideo2.value?.isEditing;
});

const notEditableAndCompletelyEmpty = computed(() => {
  return !props.editable &&
    !props.candidate?.about &&
    !props.candidate?.profileVideo1?.prompt &&
    !props.candidate?.profileVideo1?.path &&
    !props.candidate?.profileVideo2?.prompt &&
    !props.candidate?.profileVideo2?.path &&
    !props.candidate?.exceptionalAbility?.length &&
    !props.candidate?.summary?.prompt &&
    !props.candidate?.summary?.answer;
});

const containerClasses = computed(() => {
  const baseClasses = props.continuous ? "px-4 md:px-6 mt-2 pb-4" : "relative shadow-sm mt-6 pb-8";
  return baseClasses + (props.small ? " text-sm" : " text-base");
});
</script>
